import { Button, IconButton, Select } from '@ghs/components';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { Box, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import type { StidSetupState } from './types';

const AVAILABLE_ROWS = 8;
const ACTIVE_ROWS = 4;
const ROW_HEIGHT = 40;
const AVAILABLE_HEIGHT = AVAILABLE_ROWS * ROW_HEIGHT;
const ACTIVE_HEIGHT = ACTIVE_ROWS * ROW_HEIGHT;

type Props = {
  stidSetup: StidSetupState;
  onAvailableStidSelected: (stid: string) => void;
  onActiveStidAdded: () => void;
  onActiveStidSelected: (stid: string) => void;
  onActiveStidRemoved: () => void;
  onActiveMailerIdChanged: (mailerId: string) => void;
  onStidPairSaved: () => void;
  onStidPairRemoved: (index: number) => void;
};

export default function StidMailerIdSetup({
  stidSetup,
  onAvailableStidSelected,
  onActiveStidAdded,
  onActiveStidSelected,
  onActiveStidRemoved,
  onActiveMailerIdChanged,
  onStidPairSaved,
  onStidPairRemoved
}: Props) {
  const { availableStids, selectedAvailableStid, activeStids, selectedActiveStid, activeMailerId, availableMailerIds, savedPairs } = stidSetup;

  const canAdd = Boolean(selectedAvailableStid);
  const canRemove = Boolean(selectedActiveStid);
  const canSave = Boolean(selectedActiveStid) && Boolean(activeMailerId);

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="subtitle2">STID / Mailer ID Setup</Typography>
        <Typography variant="caption" fontStyle="italic" color="text.secondary">
          STIDs populate automatically from the options selected above
        </Typography>
      </Stack>

      {/* Two-column layout with transfer buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'flex-start' }}>
        {/* Left column: Available STIDs */}
        <Box sx={{ flex: 1 }}>
          {/* Available STIDs Table */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" textTransform="uppercase" fontWeight="bold">
                  Available STIDs
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: '50%',
                    minWidth: 22,
                    height: 22,
                    fontSize: 12,
                    fontWeight: 'bold'
                  }}
                >
                  {availableStids.length}
                </Box>
              </Stack>
            </Box>
            <Box sx={{ height: AVAILABLE_HEIGHT, overflow: 'auto' }}>
              {availableStids.length === 0 ? (
                <Box sx={{ p: 2, height: AVAILABLE_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    No STIDs
                  </Typography>
                </Box>
              ) : (
                availableStids.map((stid) => (
                  <Box
                    key={stid}
                    sx={{
                      cursor: 'pointer',
                      p: 1,
                      px: 2,
                      height: ROW_HEIGHT,
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: selectedAvailableStid === stid ? 'action.selected' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                    onClick={() => onAvailableStidSelected(stid)}
                  >
                    <Typography variant="body2">{stid}</Typography>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>

        {/* Transfer buttons */}
        <Stack direction="column" spacing={1} sx={{ justifyContent: 'center', pt: 5 }}>
          <IconButton onClick={onActiveStidAdded} disabled={!canAdd} color="primary" size="small" aria-label="Add selected STID">
            <ChevronRightIcon />
          </IconButton>
          <IconButton onClick={onActiveStidRemoved} disabled={!canRemove} color="primary" size="small" aria-label="Remove selected active STID">
            <ChevronLeftIcon />
          </IconButton>
        </Stack>

        {/* Right column: Active STIDs + Mailer ID */}
        <Box sx={{ flex: 1 }}>
          {/* Active STIDs Table */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', mb: 2 }}>
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" textTransform="uppercase" fontWeight="bold">
                  Active
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: '50%',
                    minWidth: 22,
                    height: 22,
                    fontSize: 12,
                    fontWeight: 'bold'
                  }}
                >
                  {activeStids.length}
                </Box>
              </Stack>
            </Box>
            <Box sx={{ height: ACTIVE_HEIGHT, overflow: 'auto' }}>
              {activeStids.length === 0 ? (
                <Box sx={{ p: 2, height: ACTIVE_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    Select an STID and click add
                  </Typography>
                </Box>
              ) : (
                activeStids.map((stid) => (
                  <Box
                    key={stid}
                    sx={{
                      cursor: 'pointer',
                      p: 1,
                      px: 2,
                      height: ROW_HEIGHT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: selectedActiveStid === stid ? 'action.selected' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                    onClick={() => onActiveStidSelected(stid)}
                  >
                    <Typography variant="body2">{stid}</Typography>
                    {selectedActiveStid === stid && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onActiveStidRemoved();
                        }}
                        aria-label={`Remove active STID ${stid}`}
                        sx={{ ml: 1 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Box>

          {/* Mailer ID Selection - Always visible */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Mailer ID {selectedActiveStid && `for ${selectedActiveStid}`}
            </Typography>
            <Select
              id="mailer-id"
              label="Select Mailer ID..."
              value={activeMailerId ?? ''}
              onChange={(e) => onActiveMailerIdChanged(e.target.value as string)}
              emptyText="Select Mailer ID..."
              formSx={{ width: '100%', mb: 1 }}
              variant="outlined"
              size="small"
              disabled={!selectedActiveStid}
            >
              {availableMailerIds.map((id) => (
                <MenuItem key={id} value={id}>
                  {id}
                </MenuItem>
              ))}
            </Select>
            <Button variant="contained" onClick={onStidPairSaved} disabled={!canSave} fullWidth size="small">
              Save Pair
            </Button>
          </Box>
        </Box>
      </Stack>

      {/* Saved pairs table */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight="bold">
            Saved STID / Mailer ID pairs
          </Typography>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: '50%',
              minWidth: 22,
              height: 22,
              fontSize: 12,
              fontWeight: 'bold'
            }}
          >
            {savedPairs.length}
          </Box>
        </Stack>

        {savedPairs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            No pairs saved yet. Select an Active STID, choose its Mailer ID, then Save Pair.
          </Typography>
        ) : (
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>STID</TableCell>
                  <TableCell>Mailer ID</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {savedPairs.map((pair, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{pair.stid}</TableCell>
                    <TableCell>{pair.mailerId}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => onStidPairRemoved(idx)} aria-label={`Delete pair ${pair.stid}-${pair.mailerId}`}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
